/**
 * Error noise filters for PostHog error tracking.
 *
 * Three categories of unactionable noise:
 *
 *  1. Browser-extension errors — password managers / autofill firing into our
 *     pages and rejecting promises with characteristic strings.
 *  2. Cross-origin script errors — when a script from a different origin
 *     throws without proper CORS, the browser sanitises the error reaching
 *     `window.onerror` to the literal "Script error." with no file, line, or
 *     stack. By design, these contain zero diagnostic information, so we drop
 *     them at the SDK boundary rather than burning triage time on issues we
 *     cannot map back to source.
 *  3. ResizeObserver loop notices, which are not exceptions at all. See
 *     `isResizeObserverLoopError`.
 */

const BROWSER_EXTENSION_PATTERNS = [
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/i,
  // Crypto-wallet extensions (MetaMask, Coinbase Wallet, Phantom) race each
  // other to define `window.ethereum` and throw when a rival has already
  // frozen or removed it. Nothing in Bower touches `window.ethereum`, so any
  // reference to it in an error MESSAGE is by definition injected. Note these
  // patterns are matched against `.message` only — a stack frame naming the
  // extension is not inspected. Paged #tech-observability on 2026-08-18 as a
  // new PostHog issue.
  /window\.ethereum/i,
  // The EIP-1193/EIP-6963 provider handshake the same extensions run.
  /\b(ethereum|solana|tronWeb|web3)\b.*\b(selectedAddress|isMetaMask|providers?)\b/i,
  // Two wallets fighting over the same global: the second to load cannot
  // redefine a property the first already installed as a non-configurable
  // getter. Anchored on the wallet global so it cannot swallow our own
  // property errors.
  /Cannot (?:set|redefine) property:? (?:ethereum|solana|tronWeb|web3)\b/i,
];

export function isBrowserExtensionError(reason: unknown): boolean {
  const nonStringMsg = reason instanceof Error ? reason.message : String(reason ?? '');
  const msg = typeof reason === 'string' ? reason : nonStringMsg;

  if (msg === '' || msg === 'null' || msg === 'undefined') {
    return false;
  }

  return BROWSER_EXTENSION_PATTERNS.some((pattern) => pattern.test(msg));
}

const CROSS_ORIGIN_SCRIPT_ERROR_VALUES = new Set<string>(['Script error.', 'Script error']);

function extractMessage(reason: unknown): string {
  if (typeof reason === 'string') return reason;
  if (reason instanceof Error) return reason.message;
  return '';
}

export function isCrossOriginScriptError(reason: unknown): boolean {
  return CROSS_ORIGIN_SCRIPT_ERROR_VALUES.has(extractMessage(reason));
}

/**
 * Not an exception: the browser reports this on `window.onerror` when a
 * ResizeObserver callback changed layout, so the observations left over were
 * deferred to the next frame rather than dropped. It converges, and it carries
 * no stack, no filename and no Error object (PostHog tags it `Synthetic`), so
 * like "Script error." there is nothing here to map back to a line.
 *
 * It is emitted by design rather than by accident, which is why this is a
 * filter and not a bug report. The table node view writes layout from inside
 * its own observer deliberately (the page-gutter bleed and the top scrollbar
 * have to be right in the frame the table is first laid out, and deferring
 * them past delivery costs a paint), and third-party observers we do not own,
 * Chakra's positioning and TipTap among them, do the same.
 *
 * Both spellings are matched: current Chrome, Firefox and Safari report the
 * "undelivered notifications" wording, older Chrome the "loop limit exceeded"
 * one. Anchored at the start of the message so a real failure of ours that
 * merely mentions a ResizeObserver is still reported.
 */
const RESIZE_OBSERVER_LOOP_PATTERN =
  /^(?:Uncaught )?ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/i;

export function isResizeObserverLoopError(reason: unknown): boolean {
  return RESIZE_OBSERVER_LOOP_PATTERN.test(extractMessage(reason));
}
